import { isEqual } from "lodash";

import type { PatientEncounter } from "@/resources/types";
import type { MevSummaryAutoOpenConfig } from "@/util/printable-obschart/config/chartdef";

type FormLikeValues = Record<string, any>;

const isMomentLike = (value: any): value is { isValid: () => boolean; valueOf: () => number } => {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof value.isValid === "function" &&
    typeof value.valueOf === "function"
  );
};

const normalizeFieldValue = (value: any) => {
  if (value === "") return undefined;

  if (isMomentLike(value)) {
    return value.isValid() ? value.valueOf() : undefined;
  }

  return value;
};

const hasAtsScore = (encounter?: Pick<PatientEncounter, "atsScore"> | null) => {
  const rawAtsScore = encounter?.atsScore;
  if (rawAtsScore === undefined || rawAtsScore === null) return false;

  const numericAts = Number(rawAtsScore);
  return Number.isFinite(numericAts) && numericAts > 0;
};

const hasAnyTriggerFieldChanged = (
  triggerFieldKeys: string[],
  originalFormValues: FormLikeValues | null | undefined,
  currentFormValues: FormLikeValues,
) => {
  if (triggerFieldKeys.length === 0) return false;

  return triggerFieldKeys.some((fieldKey) => {
    const originalValue = normalizeFieldValue(originalFormValues?.[fieldKey]);
    const currentValue = normalizeFieldValue(currentFormValues?.[fieldKey]);
    return !isEqual(originalValue, currentValue);
  });
};

export interface ShouldAutoOpenMevSummaryAfterSaveParams {
  mode: "create" | "update";
  savedEncounter?: Pick<PatientEncounter, "atsScore"> | null;
  configId?: string;
  resumeExpectedEncounter?: boolean;
  originalFormValues?: FormLikeValues | null;
  currentFormValues: FormLikeValues;
  config: MevSummaryAutoOpenConfig;
}

export interface ShouldAutoOpenDeferredMevSummaryParams {
  savedEncounter?: Pick<PatientEncounter, "atsScore"> | null;
  configId?: string;
  resumeExpectedEncounter?: boolean;
  deferredReady: boolean;
  config: MevSummaryAutoOpenConfig;
}

const passesSharedGuards = ({
  savedEncounter,
  configId,
  resumeExpectedEncounter,
  config,
}: {
  savedEncounter?: Pick<PatientEncounter, "atsScore"> | null;
  configId?: string;
  resumeExpectedEncounter?: boolean;
  config: MevSummaryAutoOpenConfig;
}) => {
  if (!config.enabled) return false;
  if (config.excludeConcierge && configId === "concierge") return false;
  if (!config.allowResumeExpected && !!resumeExpectedEncounter) return false;
  if (!hasAtsScore(savedEncounter)) return false;

  return true;
};

export const shouldAutoOpenMevSummaryAfterSave = (
  params: ShouldAutoOpenMevSummaryAfterSaveParams,
) => {
  const {
    mode,
    savedEncounter,
    configId,
    resumeExpectedEncounter,
    originalFormValues,
    currentFormValues,
    config,
  } = params;

  if (!passesSharedGuards({
    savedEncounter,
    configId,
    resumeExpectedEncounter,
    config,
  })) {
    return false;
  }

  if (mode === "create") return true;

  return hasAnyTriggerFieldChanged(
    config.triggerFieldKeys,
    originalFormValues,
    currentFormValues,
  );
};

export const shouldAutoOpenDeferredMevSummary = (
  params: ShouldAutoOpenDeferredMevSummaryParams,
) => {
  const {
    savedEncounter,
    configId,
    resumeExpectedEncounter,
    deferredReady,
    config,
  } = params;

  if (!deferredReady) return false;

  return passesSharedGuards({
    savedEncounter,
    configId,
    resumeExpectedEncounter,
    config,
  });
};
